/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* eslint-disable no-unused-vars */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import useBasket from '../../commerce-api/hooks/useBasket'
import {Flex, Stack, Text} from '@chakra-ui/react'
import {useMaterialVariation} from '../../hooks/use-variation-attributes'
import {useItemVariant} from './'
import PromoPopover from '../promo-popover'
import {useCurrency} from '../../hooks'
import useEffectEvent from '../../hooks/use-effect-event'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, currency, ...props}) => {
    const variant = useItemVariant()
    const basket = useBasket()
    const [promos, setPromos] = useState([])
    const {currency: activeCurrency} = useCurrency()
    const {c_engraving: engravingText} = variant

    const materialVariation = useMaterialVariation(variant)

    // Create a mapping of variation values to their associated attributes. This allows us
    // the render the readable names/labels rather than variation value IDs.
    const variationValues = [
        // include material variation value if it exists
        materialVariation
            ? {
                  id: materialVariation.id,
                  name: materialVariation.name,
                  value: materialVariation.selectedValue.name
              }
            : null,
        ...Object.entries(variant.variationValues || {}).map(([key, value]) => {
            const attr = variant?.variationAttributes?.find((attr) => attr.id === key)
            return {
                id: key,
                name: attr?.name || key,
                value: attr.values.find((val) => val.value === value)?.name || value
            }
        })
    ]
        // filter out null values
        .filter(Boolean)

    // Fetch all the promotions given by price adjustments. We display this info in
    // the promotion info popover when applicable.
    const onPriceAdjustmentsChange = useEffectEvent(async (priceAdjustments) => {
        let ids
        if (priceAdjustments?.length > 0) {
            ids = priceAdjustments
                .map((adj) => adj.promotionId)
                .filter((id) => {
                    return id && !promos.find((promo) => promo.id === id)
                })
        }
        if (ids && ids.length > 0) {
            const promos = await basket.getPromotions(ids)
            if (promos?.data) {
                setPromos(promos.data)
            }
        }
    })
    useEffect(() => {
        onPriceAdjustmentsChange(variant.priceAdjustments)
    }, [variant.priceAdjustments, onPriceAdjustmentsChange])

    return (
        <Stack spacing={2} flex={1} {...props} textStyle="Regular Small">
            {variationValues?.map((variationValue) => (
                <Text key={variationValue.id}>
                    {variationValue.name}: {variationValue.value}
                </Text>
            ))}

            {includeQuantity && (
                <Text>
                    <FormattedMessage
                        defaultMessage="Quantity: {quantity}"
                        values={{quantity: variant.quantity}}
                        id="item_attributes.label.quantity"
                    />
                </Text>
            )}

            {engravingText && (
                <Text>
                    <FormattedMessage
                        defaultMessage="Text: {engraving}"
                        values={{engraving: engravingText}}
                        id="item_attributes.label.engraving"
                    />
                </Text>
            )}

            {variant.priceAdjustments?.length > 0 && !variant.bonusProductLineItem && (
                <Flex alignItems="center">
                    <Text>
                        <FormattedMessage
                            defaultMessage="Promotions"
                            id="item_attributes.label.promotions"
                        />
                        {': '}
                        <Text as="span" color="stokkeFunctional.alertGreen">
                            <FormattedNumber
                                style="currency"
                                currency={currency || basket.currency || activeCurrency}
                                value={variant.priceAdjustments[0].price}
                            />
                        </Text>
                    </Text>
                </Flex>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    currency: PropTypes.string
}

export default ItemAttributes
